import CobraSpinner from '../shared/CobraSpinner';
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react';
import '@livekit/components-styles';
import { Box, Text, Center, Button, useToast, useBreakpointValue, Icon } from '@chakra-ui/react';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';
import io from 'socket.io-client';

import CustomMeetingLayout from './CustomMeetingLayout';

// Microphone icon component
const MicOffIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M19 11c0 1.19-.34 2.3-.9 3.28l-1.23-1.23c.27-.62.43-1.31.43-2.05H19zm-4 .16L9 5.18V5a3 3 0 0 1 6 0v6c0 .06 0 .11-.01.16zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
  </Icon>
);

export default function MeetingRoom() {
  const { roomName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState("");
  const [error, setError] = useState(null);
  const [updateDownloadUrl, setUpdateDownloadUrl] = useState(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [socket, setSocket] = useState(null);
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Microphone permission state: 'checking' | 'granted' | 'prompt' | 'denied'
  const [micPermission, setMicPermission] = useState('checking');
  const [micRequestLoading, setMicRequestLoading] = useState(false);

  // Check microphone permission on mount
  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({ name: 'microphone' });
          setMicPermission(result.state);
          result.onchange = () => {
            setMicPermission(result.state);
          };
          return;
        }
      } catch {
        // Permissions API not supported for microphone
      }

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasMicLabel = devices.some(d => d.kind === 'audioinput' && d.label);
        setMicPermission(hasMicLabel ? 'granted' : 'prompt');
      } catch {
        setMicPermission('prompt');
      }
    };

    checkMicPermission();
  }, []);

  // Request microphone authorization
  const handleRequestMic = useCallback(async () => {
    setMicRequestLoading(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      setMicPermission('granted');
      toast({
        title: 'تم تفعيل الميكروفون',
        description: 'يمكنك الآن استخدام الميكروفون في الاجتماع.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Mic permission error:', err);
      setMicPermission('prompt');
    } finally {
      setMicRequestLoading(false);
    }
  }, [toast]);

  const showMicPopup = micPermission === 'prompt' || micPermission === 'denied';

  const livekitUrl = process.env.REACT_APP_LIVEKIT_URL || 'wss://livekit.cobra-education.com';
  const backendUrl = process.env.REACT_APP_API_URL || 'https://api.cobra-education.com';

  // Get secretKey from navigation state (passed from MeetingsPage)
  const secretKey = location.state?.secretKey || "";

  const [canonicalRoomName, setCanonicalRoomName] = useState(roomName);

  useEffect(() => {
    if (!user) return;

    let activeSocket = null;

    const fetchToken = async (keyToUse = secretKey) => {
      try {
        const response = await axiosInstance.post(`/meetings/token`, {
          meetingId: roomName,
          secretKey: keyToUse
        });
        if (response.data.success) {
          setToken(response.data.token);
          const realRoom = response.data.roomName || roomName;
          setCanonicalRoomName(realRoom);
          if (activeSocket) {
            activeSocket.emit("join_meeting_room", { roomName: realRoom, user });
          }
        } else {
          setError(response.data.error || "Failed to get token");
        }
      } catch (err) {
        console.error(err);
        if (err.response?.data?.updateRequired) {
          setError(err.response.data.error || "يجب تحديث التطبيق إلى آخر إصدار لتتمكن من الدخول إلى الحصة.");
          setUpdateDownloadUrl(err.response.data.downloadUrl);
        } else {
          setError(err.response?.data?.error || "خطأ في الانضمام إلى الحصة. تأكد من الرقم السري أو أن الحصة لا تزال نشطة.");
        }
      }
    };

    const newSocket = io(backendUrl);
    activeSocket = newSocket;
    setSocket(newSocket);

    // Join the meeting socket room for all participants (teacher, admin, and students)
    const joinRoom = () => {
      newSocket.emit("join_meeting_room", { roomName, user });
      if (canonicalRoomName && canonicalRoomName !== roomName) {
        newSocket.emit("join_meeting_room", { roomName: canonicalRoomName, user });
      }
    };

    newSocket.on("connect", joinRoom);
    if (newSocket.connected) {
      joinRoom();
    }

    fetchToken();

    return () => {
      newSocket.off("connect", joinRoom);
      newSocket.disconnect();
    };
  }, [roomName, secretKey, user, backendUrl, canonicalRoomName]);

  // --- Live Attendance Tracking State (Students Only) ---
  const isStudent = user && user.role === 'student';
  const [totalTime, setTotalTime] = useState(0);
  const [presentTime, setPresentTime] = useState(0);
  const [absentTime, setAbsentTime] = useState(0);
  const [isPopupActive, setIsPopupActive] = useState(false);
  const [popupActiveDuration, setPopupActiveDuration] = useState(0);

  // Refs for tracking without stale closures in intervals/listeners
  const sessionIdRef = useRef(`${user?._id || 'student'}_${roomName}_${Date.now()}`);
  const isWatchingRef = useRef(typeof document !== 'undefined' ? !document.hidden : true);
  const currentStatusRef = useRef('present');
  const timelineRef = useRef([{ status: 'present', timestamp: Date.now() }]);
  const totalTimeRef = useRef(0);
  const presentTimeRef = useRef(0);
  const absentTimeRef = useRef(0);
  const isPopupActiveRef = useRef(false);
  const popupDurationRef = useRef(0);
  const hasEndedRef = useRef(false);

  // Sync latest ref values with states
  useEffect(() => {
    isPopupActiveRef.current = isPopupActive;
  }, [isPopupActive]);

  useEffect(() => {
    popupDurationRef.current = popupActiveDuration;
  }, [popupActiveDuration]);

  // Helper to transition timeline status and sync
  const setWatchingStatus = useCallback((newStatus) => {
    if (currentStatusRef.current !== newStatus) {
      currentStatusRef.current = newStatus;
      timelineRef.current.push({ status: newStatus, timestamp: Date.now() });
    }
  }, []);

  // Submit / Sync function to backend — uses XHR for max Electron compatibility
  const syncAttendance = useCallback((isFinal = false) => {
    // Only track attendance for students (skip admin, instructor, etc.)
    if (!user || user.role !== 'student') return;
    const userToken = localStorage.getItem('userToken');
    if (!userToken) return;

    if (isFinal) {
      if (hasEndedRef.current) return;
      hasEndedRef.current = true;
      timelineRef.current.push({ status: 'end', timestamp: Date.now() });
    }

    const payload = JSON.stringify({
      meetingId: roomName,
      sessionId: sessionIdRef.current,
      sessionPresentDuration: presentTimeRef.current,
      sessionAbsentDuration: absentTimeRef.current,
      sessionTotalDuration: totalTimeRef.current,
      timeline: timelineRef.current
    });

    const apiUrl = 'https://api.cobra-education.com/api/live-attendance/submit';

    try {
      // Use synchronous XHR for final/exit (window is closing), async for heartbeats
      const xhr = new XMLHttpRequest();
      xhr.open('POST', apiUrl, !isFinal); // false = synchronous for final
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Authorization', `Bearer ${userToken}`);
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log('[LiveAttendance] ✅ Synced:', xhr.status, presentTimeRef.current + 's present');
        } else {
          console.warn('[LiveAttendance] ⚠️ Server error:', xhr.status, xhr.responseText);
        }
      };
      xhr.onerror = () => {
        console.warn('[LiveAttendance] ❌ Network error on sync');
      };
      xhr.send(payload);
    } catch (err) {
      console.warn('[LiveAttendance] ❌ Sync exception:', err.message);
    }
  }, [user, roomName]);

  // Initial sync immediately when entering the room (students only)
  useEffect(() => {
    if (isStudent) {
      const timer = setTimeout(() => syncAttendance(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isStudent, syncAttendance]);

  // Window Focus / Blur & Visibility Change Listeners (Tracking if student is actively watching)
  useEffect(() => {
    if (!isStudent) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden -> Student stopped watching! Note as absence.
        isWatchingRef.current = false;
        setWatchingStatus('absent');
        syncAttendance(false);
      } else {
        // Tab visible -> Check if popup is not expired
        if (!isPopupActiveRef.current || popupDurationRef.current <= 5) {
          isWatchingRef.current = true;
          setWatchingStatus('present');
          syncAttendance(false);
        }
      }
    };

    const handleWindowBlur = () => {
      // User switched window or clicked outside -> stopped watching
      isWatchingRef.current = false;
      setWatchingStatus('absent');
      syncAttendance(false);
    };

    const handleWindowFocus = () => {
      // User returned to window
      if (!document.hidden && (!isPopupActiveRef.current || popupDurationRef.current <= 5)) {
        isWatchingRef.current = true;
        setWatchingStatus('present');
        syncAttendance(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    const handlePageHide = () => {
      syncAttendance(true);
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
      syncAttendance(true);
    };
  }, [isStudent, setWatchingStatus, syncAttendance]);

  // --- Live Attendance Tracking Logic (1-second tick & periodic auto-sync) ---
  useEffect(() => {
    if (!isStudent) return;

    let tickCount = 0;

    const timer = setInterval(() => {
      tickCount += 1;

      // 1. Update total elapsed time in room
      totalTimeRef.current += 1;
      setTotalTime(totalTimeRef.current);

      // Trigger presence check popup every 10 minutes (600 seconds) for students
      if (totalTimeRef.current > 0 && totalTimeRef.current % 600 === 0) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => { });
        }
        setIsPopupActive(true);
        setPopupActiveDuration(0);
        popupDurationRef.current = 0;
      }

      // 2. Check if student is actively watching or absent
      let currentlyWatching = isWatchingRef.current;

      if (isPopupActiveRef.current) {
        popupDurationRef.current += 1;
        setPopupActiveDuration(popupDurationRef.current);

        // If popup was unconfirmed for more than 5 seconds -> count as absence
        if (popupDurationRef.current > 5) {
          currentlyWatching = false;
        }
      }

      if (currentlyWatching) {
        presentTimeRef.current += 1;
        setPresentTime(presentTimeRef.current);
        setWatchingStatus('present');
      } else {
        absentTimeRef.current += 1;
        setAbsentTime(absentTimeRef.current);
        setWatchingStatus('absent');
      }

      // Auto-sync heartbeat every 10 seconds
      if (tickCount % 10 === 0) {
        syncAttendance(false);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isStudent, setWatchingStatus, syncAttendance]);

  const handleIAmPresentClick = () => {
    setIsPopupActive(false);
    setPopupActiveDuration(0);
    popupDurationRef.current = 0;
    if (!document.hidden) {
      isWatchingRef.current = true;
      setWatchingStatus('present');
      syncAttendance(false);
    }
  };
  // --- End Attendance Logic ---

  if (error) {
    return (
      <Center h="100vh" flexDirection="column" bg="gray.900" color="white" p={6} textAlign="center" dir="rtl">
        <Text fontSize="xl" fontWeight="bold" color="red.400" mb={4} maxW="500px">{error}</Text>
        {updateDownloadUrl && (
          <Button
            as="a"
            href={updateDownloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            colorScheme="yellow"
            size="lg"
            mb={4}
            fontWeight="bold"
            fontFamily="Tajawal, sans-serif"
          >
            تحميل التحديث الآن
          </Button>
        )}
        <Button variant="outline" colorScheme="gray" onClick={() => navigate(-1)}>العودة</Button>
      </Center>
    );
  }

  if (isWaiting) {
    return (
      <Center h="100vh" flexDirection="column" bg="gray.900" color="white">
        <CobraSpinner size="xl" color="orange.400" mb={4} />
        <Text fontSize="xl">أنت في غرفة الانتظار...</Text>
        <Text fontSize="md" color="gray.600">يرجى الانتظار حتى يسمح لك المعلم بالدخول</Text>
      </Center>
    );
  }

  if (!token) {
    return (
      <Center h="100vh" bg="gray.900" color="white">
        <CobraSpinner size="xl" color="teal.400" />
        <Text ml={4}>جاري الانضمام...</Text>
      </Center>
    );
  }

  return (
    <Box position="fixed" top={0} left={0} bottom={0} right={0} w="100vw" h="100vh" overflow="hidden" bg="#090d16" zIndex={99999}>
      <style>{`
        .my-custom-livekit-room {
          /* Light Mode Colors */
          --lk-bg: #ffffff;
          --lk-control-bar-bg: #f7f9fa;
          --lk-accent: #0b5cff;
          --lk-accent-fg: #ffffff;
          --lk-danger: #ff2a2a;
          --lk-font-family: 'Tajawal', sans-serif;
          --lk-border-radius: 8px;
        }

        /* Control Bar Light Zoom Look */
        .lk-control-bar {
          background-color: #f7f9fa !important;
          border-top: 1px solid #e2e8f0 !important;
          padding: 8px 16px !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          gap: 12px !important;
          height: auto !important;
          direction: ltr !important; /* Force LTR for exact Zoom order */
          position: relative !important;
          width: 100% !important;
        }

        /* Group Buttons into columns (Icon on top, Text below) */
        .lk-control-bar .lk-button:not(.lk-button-menu) {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          background-color: transparent !important;
          border: none !important;
          border-radius: 6px !important;
          padding: 6px 8px !important;
          min-width: 65px !important;
          height: auto !important;
          color: #333333 !important; /* Light mode text */
          gap: 4px !important;
          transition: background-color 0.2s ease !important;
        }
        
        .lk-control-bar .lk-button:not(.lk-button-menu):hover {
          background-color: rgba(0, 0, 0, 0.05) !important;
        }

        .lk-control-bar .lk-button svg {
          width: 20px !important;
          height: 20px !important;
        }

        /* Fix Caret Menu Buttons */
        .lk-control-bar .lk-button-menu {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 24px !important;
          min-width: 24px !important;
          padding: 0 !important;
          background: transparent !important;
          border: none !important;
          color: #333333 !important;
          height: 100% !important;
        }
        .lk-control-bar .lk-button-menu:hover {
          background-color: rgba(0, 0, 0, 0.05) !important;
          border-radius: 4px !important;
        }

        /* Button Groups (Keep Mic+Caret and Camera+Caret together) */
        .lk-control-bar .lk-button-group {
          display: flex !important;
          align-items: stretch !important;
          background-color: transparent !important;
          border-radius: 6px !important;
          gap: 2px !important;
        }
        .lk-control-bar .lk-button-group:hover {
          background-color: rgba(0, 0, 0, 0.02) !important;
        }

        /* Position Left Controls (Mic Group, Camera Group) */
        @media (min-width: 768px) {
          .lk-control-bar > .lk-button-group:nth-of-type(1) {
            position: absolute !important;
            left: 16px !important;
          }
          .lk-control-bar > .lk-button-group:nth-of-type(2) {
            position: absolute !important;
            left: 110px !important; /* Adjusted for group width */
          }
          /* Position Right Control (End) */
          .lk-disconnect-button {
            position: absolute !important;
            right: 16px !important;
          }
        }

        @media (max-width: 767px) {
          .lk-control-bar {
            justify-content: space-evenly !important;
            gap: 4px !important;
            padding: 8px 4px !important;
          }
          .lk-control-bar > .lk-button-group {
            position: static !important;
          }
        }

        /* Hide default text nodes by setting font size to 0 on the button */
        .lk-control-bar .lk-button:not(.lk-button-menu) {
          font-size: 0 !important;
          color: transparent !important;
        }

        /* Inject Custom Zoom Texts */
        .lk-control-bar .lk-button:not(.lk-button-menu)::after {
          font-size: 11px !important;
          font-weight: 500 !important;
          color: #333333 !important; /* Light mode text */
          display: block;
          margin-top: 4px;
        }

        /* Camera / Video Texts */
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="true"]::after,
        .lk-control-bar .lk-button[data-lk-source="camera"].lk-button-active::after {
          content: "Stop Video";
        }
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="false"]::after,
        .lk-control-bar .lk-button[data-lk-source="camera"]:not([aria-pressed="true"]):not(.lk-button-active)::after {
          content: "Start Video";
        }

        /* Microphone / Audio Texts */
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="true"]::after,
        .lk-control-bar .lk-button[data-lk-source="microphone"].lk-button-active::after {
          content: "Mute";
        }
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="false"]::after,
        .lk-control-bar .lk-button[data-lk-source="microphone"]:not([aria-pressed="true"]):not(.lk-button-active)::after {
          content: "Unmute";
        }

        /* Screen Share Text */
        .lk-control-bar .lk-button[data-lk-source="screen_share"]::after {
          content: "Share Screen";
        }
        /* Screen Share Icon Zoom color (Green) */
        .lk-control-bar .lk-button[data-lk-source="screen_share"] svg {
          fill: #23d959 !important;
          color: #23d959 !important;
        }

        /* Chat Text */
        .lk-chat-toggle::after {
          content: "Chat";
        }

        /* Chat Icon */
        .lk-chat-toggle svg {
          fill: #0b5cff !important; /* Blue color */
          color: #0b5cff !important;
        }

        /* Leave Button Styling (End) */
        .lk-control-bar .lk-disconnect-button {
          background-color: #ff2a2a !important;
          color: white !important;
          margin-left: 0 !important;
          padding: 8px 16px !important;
          border-radius: 6px !important;
          font-size: 0 !important; /* hide default */
          font-weight: bold !important;
          flex-direction: row !important;
          min-width: 60px !important;
        }
        .lk-control-bar .lk-disconnect-button svg {
          display: none !important; /* Hide icon for End button to look like Zoom */
        }
        .lk-control-bar .lk-disconnect-button::after {
          content: "End";
          font-size: 13px !important;
          margin-top: 0 !important;
          color: white !important;
        }
        .lk-control-bar .lk-disconnect-button:hover {
          background-color: #d12222 !important;
        }

        /* Microphone & Camera States */
        /* Active (ON) - Dark Icon for Light Mode */
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="true"] svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="true"] svg,
        .lk-control-bar .lk-button[data-lk-source="camera"].lk-button-active svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"].lk-button-active svg {
          fill: #333333 !important;
          color: #333333 !important;
        }
        
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="true"] svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="true"] svg path,
        .lk-control-bar .lk-button[data-lk-source="camera"].lk-button-active svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"].lk-button-active svg path {
          fill: #333333 !important;
        }

        /* Inactive (OFF) - Red Icon */
        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="false"] svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="false"] svg,
        .lk-control-bar .lk-button[data-lk-source="camera"]:not([aria-pressed="true"]):not(.lk-button-active) svg, 
        .lk-control-bar .lk-button[data-lk-source="microphone"]:not([aria-pressed="true"]):not(.lk-button-active) svg {
          fill: #ff2a2a !important;
          color: #ff2a2a !important;
        }

        .lk-control-bar .lk-button[data-lk-source="camera"][aria-pressed="false"] svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"][aria-pressed="false"] svg path,
        .lk-control-bar .lk-button[data-lk-source="camera"]:not([aria-pressed="true"]):not(.lk-button-active) svg path, 
        .lk-control-bar .lk-button[data-lk-source="microphone"]:not([aria-pressed="true"]):not(.lk-button-active) svg path {
          fill: #ff2a2a !important;
        }

        /* Screen Share Active State */
        .lk-control-bar .lk-button[data-lk-source="screen_share"][aria-pressed="true"] svg,
        .lk-control-bar .lk-button[data-lk-source="screen_share"].lk-button-active svg {
          fill: #0b5cff !important;
          color: #0b5cff !important;
        }

        /* Chat Panel Styling for Light Mode */
        .lk-chat {
          border-left: 1px solid #e2e8f0 !important;
          background-color: #ffffff !important;
          color: #333333 !important;
        }
        
        .lk-chat-header {
          border-bottom: 1px solid #e2e8f0 !important;
          color: #1a202c !important;
          background-color: #f7f9fa !important;
          font-family: 'Tajawal', sans-serif;
          font-weight: bold !important;
        }

        .lk-chat-messages {
          background-color: #ffffff !important;
          position: relative !important;
        }
        
        .lk-chat-messages::before {
          content: "" !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          background-image: url('/assets/cobra.png') !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          background-size: contain !important;
          opacity: 0.1 !important;
          z-index: 0 !important;
          pointer-events: none !important;
        }
        
        /* Fix Chat Messages Text */
        .lk-chat-entry {
          color: #ffffff !important;
          position: relative !important;
          z-index: 1 !important;
        }
        .lk-chat-entry .lk-message-body {
          color: #ffffff !important;
          background-color: #0b5cff !important; /* Blue bubble so white text is readable */
          padding: 8px 12px !important;
          border-radius: 8px !important;
          display: inline-block !important;
        }
        .lk-chat-entry .lk-meta-data {
          color: #888888 !important;
        }
        .lk-chat-entry .lk-participant-name {
          color: #333333 !important;
          font-weight: 600 !important;
        }
        
        .lk-chat-form {
          border-top: 1px solid #e2e8f0 !important;
          background-color: #f7f9fa !important;
        }
        
        .lk-chat-form input {
          color: #333333 !important;
          background-color: #ffffff !important;
          border: 1px solid #cbd5e0 !important;
        }

        /* Fix Video Backgrounds for Light Mode */
        .lk-video-conference, .lk-audio-conference, .lk-focus-layout {
          background-color: #0f172a !important;
        }

        /* Full Screen LiveKit Container */
        .lk-video-conference {
          width: 100% !important;
          height: 100% !important;
          position: relative !important;
          display: flex !important;
          flex-direction: column !important;
        }

        .lk-grid-layout {
          width: 100% !important;
          height: calc(100% - 70px) !important;
          padding: 16px !important;
          gap: 16px !important;
        }

        .lk-focus-layout-wrapper {
          width: 100% !important;
          height: calc(100% - 70px) !important;
          padding: 12px !important;
        }

        /* Sleek Participant Video Cards */
        .lk-participant-tile {
          background-color: #0f172a !important;
          border: 1.5px solid rgba(255, 255, 255, 0.12) !important;
          border-radius: 16px !important;
          box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.4) !important;
          overflow: hidden !important;
          position: relative !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }

        .lk-participant-tile:hover {
          border-color: rgba(59, 130, 246, 0.6) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 14px 35px -5px rgba(0, 0, 0, 0.5) !important;
        }

        .lk-participant-tile[data-lk-speaking="true"] {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4), 0 12px 32px -5px rgba(0, 0, 0, 0.5) !important;
        }

        .lk-participant-tile video {
          border-radius: 16px !important;
          object-fit: cover !important;
        }

        /* Fix Participant Name Tag overlay */
        .lk-participant-metadata {
          background-color: rgba(15, 23, 42, 0.85) !important;
          backdrop-filter: blur(12px) !important;
          color: #ffffff !important;
          border-radius: 10px !important;
          padding: 6px 12px !important;
          border: 1px solid rgba(255, 255, 255, 0.15) !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
        }

        .lk-participant-metadata .lk-participant-name {
          color: #ffffff !important;
          font-weight: 600 !important;
        }

        .lk-participant-metadata svg {
          fill: #ffffff !important;
        }

        /* Fix Indicator/Metadata Backgrounds */
        .lk-connection-quality, .lk-audio-visualizer {
          background-color: rgba(15, 23, 42, 0.85) !important;
          backdrop-filter: blur(8px) !important;
          color: #ffffff !important;
          border: 1px solid rgba(255, 255, 255, 0.15) !important;
          border-radius: 8px !important;
        }
      `}</style>
      <LiveKitRoom
        video={true}
        audio={(() => {
          // Instructors and admins join muted by default so they can multitask without noise
          const role = user?.role?.toLowerCase();
          const isStaff = role === 'admin' || role === 'instructor' || role === 'teacher';
          return !isStaff;
        })()}
        token={token}
        serverUrl={livekitUrl}
        data-lk-theme="default"
        className="my-custom-livekit-room"
        onDisconnected={() => {
          syncAttendance(true);
          navigate(-1);
        }}
        style={{ height: '100%', width: '100%' }}
      >
        <RoomAudioRenderer />
        {socket && <CustomMeetingLayout socket={socket} roomName={canonicalRoomName || roomName} secretKey={secretKey} user={user} />}
      </LiveKitRoom>

      {/* Microphone Authorization Banner */}
      {showMicPopup && (
        <Box
          position="absolute"
          top="16px"
          left="50%"
          transform="translateX(-50%)"
          zIndex={100}
          bg="rgba(17, 20, 24, 0.95)"
          border="1px solid"
          borderColor="orange.400"
          borderRadius="14px"
          boxShadow="0 4px 30px rgba(255, 184, 0, 0.2)"
          px={5}
          py={3}
          display="flex"
          alignItems="center"
          gap={3}
          dir="rtl"
          backdropFilter="blur(10px)"
          maxW="90vw"
        >
          <MicOffIcon boxSize={6} color="orange.400" />
          <Text color="white" fontSize="sm" fontFamily="'Tajawal', sans-serif" whiteSpace="nowrap">
            الميكروفون غير مفعّل
          </Text>
          <Button
            size="sm"
            onClick={handleRequestMic}
            isLoading={micRequestLoading}
            loadingText="..."
            bg="orange.400"
            color="black"
            fontWeight="bold"
            fontFamily="'Tajawal', sans-serif"
            borderRadius="10px"
            px={4}
            _hover={{ bg: 'orange.300', boxShadow: '0 2px 12px rgba(255, 184, 0, 0.3)' }}
            _active={{ bg: 'orange.500' }}
            transition="all 0.2s ease"
          >
            تفعيل الميكروفون
          </Button>
        </Box>
      )}
    </Box>
  );
}

